import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 7 - 깔끔한 회색 톤의 전문적인 디자인
const Template7 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-7 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header */}
      <div className="p-8 pb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className={getPlaceholderClass(hasRealData.name, "text-3xl font-bold mb-1 text-gray-800", "text-3xl font-bold mb-1 text-gray-300 italic")}>
              {currentData.name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, "text-lg uppercase tracking-wider text-gray-600", "text-lg uppercase tracking-wider text-gray-300 italic")}>
              {currentData.title}
            </p>
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="mt-4">
          <ContactInfo
            phone={currentData.phone}
            email={currentData.email}
            address={currentData.address}
            socialLinks={currentData.socialLinks}
            hasRealData={hasRealData}
            direction="horizontal"
            iconSize="w-3 h-3"
          />
        </div>
      </div>

      {/* Professional Summary */}
      {currentData.summary && (
        <div className="px-8 pb-6">
          <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
            {titles.professionalSummary || titles.introduction}
          </div>
          <div className="mt-4">
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm leading-relaxed text-gray-700"
            />
          </div>
        </div>
      )}

      {/* Core Competencies - Full Width */}
      {currentData.keyStrengths.length > 0 && (
        <div className="px-8 pb-6">
          <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
            {titles.coreCompetencies}
          </div>
          <div className="mt-4">
            <CoreCompetenciesSection
              keyStrengths={currentData.keyStrengths}
              hasRealData={hasRealData}
              className="space-y-1"
              itemClassName="text-sm"
              bulletStyle="•"
              bulletClassName="text-gray-700 mt-1"
              textClassName="text-gray-700"
            />
          </div>
        </div>
      )}
      {/* Career History (Experience) - Full Width */}
      <div className="px-8 pb-6">
        <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
          {titles.careerHistory || titles.experience}
        </div>
        <ExperienceSection
          experiences={currentData.experiences}
          hasRealData={hasRealData}
          itemClassName="mb-6 last:mb-0"
          datePosition="right"
          positionClassName="text-sm font-bold uppercase text-gray-700"
          companyClassName="text-sm text-gray-600"
          dateClassName="text-xs whitespace-nowrap text-gray-500"
          descriptionClassName="text-sm mt-2"
          projectTitleClassName="text-sm font-semibold text-gray-600"
          projectDescriptionClassName="text-sm mt-1"
          projectIndent=""
          bulletStyle="•"
        />
      </div>

      {/* Two Column Layout for Education and Skills */}
      <div className="px-8 pb-6 flex gap-8">
        {/* Left Column */}
        <div className="w-1/2">
          {/* Academic Background (Education) */}
          <div className="mb-6">
            <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
              {titles.academicBackground || titles.education}
            </div>
            <EducationSection
              education={currentData.education}
              hasRealData={hasRealData}
              itemClassName="mb-4 last:mb-0"
              datePosition="right"
              schoolClassName="text-sm font-bold uppercase text-gray-700"
              degreeClassName="text-sm text-gray-600"
              majorClassName="text-xs text-gray-500"
              dateClassName="text-xs whitespace-nowrap text-gray-500"
              showMajor={true}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="w-1/2">
          {/* Core Skills */}
          <div className="mb-6">
            <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
              {titles.coreSkills || titles.skills}
            </div>
            <SkillSection
              skills={currentData.skills}
              hasRealData={hasRealData}
              variant="list"
              className="space-y-1"
              itemClassName="text-sm"
              bulletStyle="•"
            />
          </div>

          {/* Certificates */}
          {currentData.certificates.length > 0 && (
            <div className="mb-6">
              <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
                {titles.certificates}
              </div>
              <CertificateSection
                certificates={currentData.certificates}
                hasRealData={hasRealData}
                itemClassName="mb-3 last:mb-0"
                nameClassName="text-sm font-semibold text-gray-700"
                issuerClassName="text-xs text-gray-600"
                dateFormat="inline"
              />
            </div>
          )}

          {/* Papers */}
          {hasRealData.papers && (
            <div>
              <div className="bg-gray-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
                {titles.papers}
              </div>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                itemClassName="mb-3 last:mb-0"
                titleClassName="text-sm font-semibold text-gray-700"
                authorsClassName="text-xs text-gray-600"
                journalClassName="text-xs text-gray-500"
                typeClassName="ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded"
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer space */}
      <div className="h-8"></div>
    </div>
  );
};

export default Template7;